import { create } from 'zustand';

const getDefaultDateRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return {
    startDate: startOfMonth.toISOString().split('T')[0],
    endDate: endOfMonth.toISOString().split('T')[0]
  };
};

export const useFilterStore = create((set, get) => ({
  // Date & Time filters
  ...getDefaultDateRange(),
  startTime: null,
  endTime: null,
  
  // Multi-select filters
  storeIds: [],
  employeeIds: [],
  categoryIds: [],
  itemIds: [],
  modifierIds: [],
  discountIds: [],
  paymentTypeIds: [],
  posDeviceIds: [],  // NEW: POS filter
  
  // View options
  viewType: 'chart', // table or chart (default to chart)
  chartType: 'line', // line or bar
  groupBy: 'day', // day, week, month, year
  reportType: 'quantity', // quantity or revenue
  splitView: false, // split data by filtered dimension (branches, items, etc.)
  splitBy: null, // 'store', 'category', 'item', 'employee', 'pos'
  
  // Period Comparison
  comparisonEnabled: false,
  comparisonStartDate: null,
  comparisonEndDate: null,
  
  // Target overlay
  selectedTargetId: null,
  showTargetLine: false,
  
  // Actions
  setDateRange: (startDate, endDate) => set({ startDate, endDate }),
  setTimeRange: (startTime, endTime) => set({ startTime, endTime }),
  setStoreIds: (storeIds) => set({ storeIds }),
  setEmployeeIds: (employeeIds) => set({ employeeIds }),
  setCategoryIds: (categoryIds) => set({ categoryIds }),
  setItemIds: (itemIds) => set({ itemIds }),
  setModifierIds: (modifierIds) => set({ modifierIds }),
  setDiscountIds: (discountIds) => set({ discountIds }),
  setPaymentTypeIds: (paymentTypeIds) => set({ paymentTypeIds }),
  setPosDeviceIds: (posDeviceIds) => set({ posDeviceIds }),  // NEW
  setViewType: (viewType) => set({ viewType }),
  setChartType: (chartType) => set({ chartType }),
  setGroupBy: (groupBy) => set({ groupBy }),
  setReportType: (reportType) => set({ reportType }),
  setSplitView: (splitView) => set({ splitView }),
  setSplitBy: (splitBy) => set({ splitBy }),
  setComparisonEnabled: (comparisonEnabled) => set({ comparisonEnabled }),
  setComparisonDateRange: (comparisonStartDate, comparisonEndDate) => set({ comparisonStartDate, comparisonEndDate }),
  setSelectedTargetId: (selectedTargetId) => set({ selectedTargetId }),
  setShowTargetLine: (showTargetLine) => set({ showTargetLine }),
  
  // Reset all filters
  resetFilters: () => set({
    ...getDefaultDateRange(),
    startTime: null,
    endTime: null,
    storeIds: [],
    employeeIds: [],
    categoryIds: [],
    itemIds: [],
    modifierIds: [],
    discountIds: [],
    paymentTypeIds: [],
    posDeviceIds: [],
  }),
  
  // Load from saved layout
  loadLayout: (layout) => set({
    ...layout.filters,
    viewType: layout.view_type,
    chartType: layout.chart_type,
    groupBy: layout.group_by,
    reportType: layout.report_type,
    comparePrevious: layout.compare_previous,
  }),
  
  // Get current state as filter payload
  getFilterPayload: () => {
    const state = get();
    return {
      start_date: state.startDate ? `${state.startDate}T00:00:00Z` : null,
      end_date: state.endDate ? `${state.endDate}T23:59:59Z` : null,
      start_time: state.startTime,
      end_time: state.endTime,
      store_ids: state.storeIds.length > 0 ? state.storeIds : null,
      employee_ids: state.employeeIds.length > 0 ? state.employeeIds : null,
      category_ids: state.categoryIds.length > 0 ? state.categoryIds : null,
      item_ids: state.itemIds.length > 0 ? state.itemIds : null,
      modifier_ids: state.modifierIds.length > 0 ? state.modifierIds : null,
      discount_ids: state.discountIds.length > 0 ? state.discountIds : null,
      payment_type_ids: state.paymentTypeIds.length > 0 ? state.paymentTypeIds : null,
      pos_device_ids: state.posDeviceIds.length > 0 ? state.posDeviceIds : null,
    };
  },
  
  // Get current state for saving layout
  getLayoutConfig: () => {
    const state = get();
    return {
      filters: {
        startDate: state.startDate,
        endDate: state.endDate,
        startTime: state.startTime,
        endTime: state.endTime,
        storeIds: state.storeIds,
        employeeIds: state.employeeIds,
        categoryIds: state.categoryIds,
        itemIds: state.itemIds,
        modifierIds: state.modifierIds,
        discountIds: state.discountIds,
        paymentTypeIds: state.paymentTypeIds,
        posDeviceIds: state.posDeviceIds,
      },
      view_type: state.viewType,
      chart_type: state.chartType,
      group_by: state.groupBy,
      report_type: state.reportType,
      compare_previous: state.comparePrevious,
    };
  }
}));
